import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {catchError, map} from 'rxjs/operators';
import {EnvConfig} from '../../services/env-config';
import {BehaviorSubject} from 'rxjs';
import {AuthService} from '../../services/auth.service';
import {SessionTimeOutModalService} from '../../shared/sessiontimeout-modal/sessiontimeout-modal.service';
import {HttpErrorHandlerService} from '../../services/http-error-handler.service';
import { POLICY_CLAIMS_BATCH_SIZE } from '../../shared/constants';

@Injectable()
export class ClaimsService {
    claimsList = [];
    private messageSource = new BehaviorSubject({});
    fnolSubmitted = this.messageSource.asObservable();
    private baseUrl = this.env.getUrl().apiURL;
    private tashUrl = this.env.getUrl().taskURL;

    constructor(
        private http: HttpClient,
        private env: EnvConfig,
        private modalService: SessionTimeOutModalService,
        private authService: AuthService,
        private errorHandler: HttpErrorHandlerService) {
    }

    newFnolData(data: any) {
        this.messageSource.next(data);
    }

    getCustomerByUsername() {
        const apiUrl = `${this.baseUrl}/proxyservice/oasis/api/customer/getCustomerByUsername`;
        const payload = {
            username: localStorage.getItem('username')
        }

        return this.http.post(apiUrl, payload,
            {
                observe: 'response',
                ...this.env.getHttpHeaders()
            }
        ).pipe(
            map((res: any) => {
                localStorage.hostServerName = res.headers.get('X-SERVER-HOSTNAME');
                return res.body;
            }),
            catchError((error) => {
                return this.errorHandler.handleError(error, this.modalService, this.authService);
            })
        );
    }

    getClaimsByPolicies(policyNumbers: string[]) {
        if (policyNumbers.length > POLICY_CLAIMS_BATCH_SIZE) {
            console.error(`Policy list size of ${policyNumbers.length} exceeds batch limit of ${POLICY_CLAIMS_BATCH_SIZE}`);
        }

        const apiUrl = `${this.baseUrl}/proxyservice/citadel/api/externalAccess/getClaimsForPolicies`;
        return this.http.post(apiUrl, policyNumbers,
            {
                observe: 'response',
                ...this.env.getHttpHeaders()
            }
        ).pipe(
            map((res: any) => {
                localStorage.hostServerName = res.headers.get('X-SERVER-HOSTNAME');
                return res.body;
            }),
            catchError((error) => {
                return this.errorHandler.handleError(error, this.modalService, this.authService);
            })
        );
    }

    getClaimsByPolicy(policyNumber: any) {
        const apiUrl = `${this.baseUrl}/proxyservice/citadel/api/externalAccess/claimsForInsuredLogin/${policyNumber}`;
        return this.http.post(apiUrl, false,
            {
                observe: 'response',
                ...this.env.getHttpHeaders()
            }
        ).pipe(
            map((res: any) => {
                localStorage.hostServerName = res.headers.get('X-SERVER-HOSTNAME');
                return res.body;
            }),
            catchError((error) => {
                return this.errorHandler.handleError(error, this.modalService, this.authService);
            })
        );
    }

    getClaimProgressStatus(claimNumber: any) {
        const apiUrl = `${this.baseUrl}/proxyservice/citadel/api/externalAccess/cpClaimBarHistory/${claimNumber}`;
        return this.http.post(apiUrl, false,
            {
                observe: 'response',
                ...this.env.getHttpHeaders()
            }
        ).pipe(
            map((res: any) => {
                localStorage.hostServerName = res.headers.get('X-SERVER-HOSTNAME');
                return res.body;
            }),
            catchError((error) => {
                return this.errorHandler.handleError(error, this.modalService, this.authService);
            })
        );
    }

    getAllClaimDocuments(claimNumber: any) {
        const apiUrl = `${this.baseUrl}/proxyservice/citadel/api/externalAccess/insured/documents/${claimNumber}`;
        return this.http.post(apiUrl, false,
            {
                observe: 'response',
                ...this.env.getHttpHeaders()
            }
        ).pipe(
            map((res: any) => {
                localStorage.hostServerName = res.headers.get('X-SERVER-HOSTNAME');
                return res.body;
            }),
            catchError((error) => {
                return this.errorHandler.handleError(error, this.modalService, this.authService);
            })
        );
    }

    sendDocumentsToImageRight(request: any) {
        const apiUrl = `${this.baseUrl}/proxyservice/citadel/api/externalAccess/insured/sendDocumentsToImageRight`;

        return this.http.post(apiUrl, request,
            {
                observe: 'response',
                ...this.env.getHttpHeaders()
            }
        ).pipe(
            map((res: any) => {
                localStorage.hostServerName = res.headers.get('X-SERVER-HOSTNAME');
                return res.body;
            }),
            catchError((error) => {
                return this.errorHandler.handleError(error, this.modalService, this.authService);
            })
        );
    }


    findDocumentRequestsByUser() {
        const apiUrl = `${this.baseUrl}/proxyservice/tasknotes/api/customerTask/findDocumentRequestsByUser`;
        const reqBody = {
            byUsername: localStorage.getItem('username')
        };

        return this.http.post(apiUrl, reqBody,
            {
                observe: 'response',
                ...this.env.getHttpHeaders()
            }
        ).pipe(
            map((res: any) => {
                localStorage.hostServerName = res.headers.get('X-SERVER-HOSTNAME');
                return res.body;
            }),
            catchError((error) => {
                return this.errorHandler.handleError(error, this.modalService, this.authService);
            })
        );
    }

    confirmAlreadyDocSent(taskId: any, adjusterUsername: any) {
        const apiUrl = `${this.baseUrl}/proxyservice/tasknotes/api/customerTask/replyOnTask`;
        const reqBody = {
            taskId: taskId,
            adjusterUsername: adjusterUsername,
            username: localStorage.getItem('username'),
            alreadySubmitted: true,
            linkedDocuments: []
        };

        return this.http.post(apiUrl, reqBody,
            {
                observe: 'response',
                ...this.env.getHttpHeaders()
            }
        ).pipe(
            map((res: any) => {
                localStorage.hostServerName = res.headers.get('X-SERVER-HOSTNAME');
                return res.body;
            }),
            catchError((error) => {
                return this.errorHandler.handleError(error, this.modalService, this.authService);
            })
        );
    }

    submitUploadedDocs(taskId: any, adjusterUsername: any, fileList: any) {
        const apiUrl = `${this.baseUrl}/proxyservice/tasknotes/api/customerTask/replyOnTask`;
        const reqBody = {
            taskId: taskId,
            adjusterUsername: adjusterUsername,
            username: localStorage.getItem('username'),
            alreadySubmitted: false,
            linkedDocuments: fileList
        }

        return this.http.post(apiUrl, reqBody,
            {
                observe: 'response',
                ...this.env.getHttpHeaders()
            }
        ).pipe(
            map((res: any) => {
                localStorage.hostServerName = res.headers.get('X-SERVER-HOSTNAME');
                return res.body;
            }),
            catchError((error) => {
                return this.errorHandler.handleError(error, this.modalService, this.authService);
            })
        );
    }

    startFnol(data: any, isExpressFnol?: any) {
        let subSystem = isExpressFnol ? "express" : "insured";
        let httpHeaders = {
            'x-session-id': localStorage?.clientSessionId,
            'x-user-id': localStorage?.username,
            'Authorization': `Bearer ${localStorage?.oauthToken}`,
            'Source': 'WEB'
        }
        const apiUrl = `${this.baseUrl}/proxyservice/citadel/api/externalAccess/${subSystem}/startFnol`;
        return this.http.post(apiUrl, data, {
            observe: 'response', headers: isExpressFnol ? {
                'Authorization': `Bearer ${localStorage.xauthToken}`,
                'Source': 'WEB'
            } : httpHeaders
        })
            .pipe(
                map((res: any) => {
                    if (!isExpressFnol) {
                        localStorage.hostServerName = res?.headers?.get('X-SERVER-HOSTNAME');
                    }
                    return res;
                }),
                catchError((error) => {
                    return this.errorHandler.handleError(error, this.modalService, this.authService, isExpressFnol);
                })
            );
    }

    saveFnol(data: any, isExpressFnol?: any) {
        let subSystem = isExpressFnol ? "express" : "insured";
        let httpHeaders = {
            'Content-Type': 'application/json',
            'x-auth-token': localStorage.claimSessionId,
            'x-session-id': localStorage.clientSessionId,
            'x-user-id': localStorage.username,
            'Authorization': `Bearer ${localStorage.oauthToken}`,
            'Source': 'WEB'
        }
        const apiUrl = `${this.baseUrl}/proxyservice/citadel/api/externalAccess/${subSystem}/change`;
        return this.http.post(apiUrl, data, {
            observe: 'response', headers: isExpressFnol ? {
                'Content-Type': 'application/json',
                'x-auth-token': localStorage.claimSessionId,
                'x-session-id': localStorage.clientSessionId,
                'Authorization': `Bearer ${localStorage.xauthToken}`,
                'Source': 'WEB'
            } : httpHeaders
        })
            .pipe(
                map((res: any) => {
                    if (!isExpressFnol) {
                        localStorage.hostServerName = res?.headers?.get('X-SERVER-HOSTNAME');
                    }
                    return res.body;
                }),
                catchError((error) => {
                    return this.errorHandler.handleError(error, this.modalService, this.authService, isExpressFnol);
                })
            );
    }

    exitFnol(isExpressFnol?: any) {
        let subSystem = isExpressFnol ? "express" : "insured";
        let httpHeaders = {
            'Content-Type': 'application/json',
            'x-auth-token': localStorage.claimSessionId,
            'x-session-id': localStorage.clientSessionId,
            'x-user-id': localStorage.username,
            'Authorization': `Bearer ${localStorage.oauthToken}`,
            'Source': 'WEB'
        }
        const apiUrl = `${this.baseUrl}/proxyservice/citadel/api/externalAccess/${subSystem}/exitFnol`;
        return this.http.post(apiUrl, {},
            {
                observe: 'response', headers: isExpressFnol ? {
                    'Content-Type': 'application/json',
                    'x-auth-token': localStorage.claimSessionId,
                    'x-session-id': localStorage.clientSessionId,
                    'Authorization': `Bearer ${localStorage.xauthToken}`,
                    'Source': 'WEB'
                } : httpHeaders
            })
            .pipe(
                map((res: any) => {
                    if (!isExpressFnol) {
                        localStorage.hostServerName = res?.headers?.get('X-SERVER-HOSTNAME');
                    }
                    return res.body;
                }),
                catchError((error) => {
                    return this.errorHandler.handleError(error, this.modalService, this.authService, isExpressFnol);
                })
            );
    }

    downloadFileByUrl(url: string) {
        return this.http.get(url, {responseType: 'blob'});
    }
}
